import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from '../../Authservices/axiosInstance';
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { Button, Alert, Modal } from "react-bootstrap";
import { IoInformationCircle } from "react-icons/io5";
import { MdEdit, MdDelete, MdAdd, MdPerson, MdClose, MdCloudUpload } from "react-icons/md";
import { canSeeSalary, canChangeRole, canEditEmployee } from "../../utils/permissions";
import "./edit-emp.css";

// Defined OUTSIDE the component so it is not re-created on every render.
// (When it was inside, the input lost focus after every keystroke.)
const UKDateInput = ({ label, value, onChange, name, required }) => (
  <Form.Group controlId={`formGrid${name}`}>
    <Form.Label>{label} {required && <span className="required">*</span>}:</Form.Label>
    <Form.Control type="text" name={name} value={value} onChange={(e) => onChange(name, e.target.value)} required={required} placeholder="DD/MM/YYYY" />
    <Form.Text className="text-muted">Format: DD/MM/YYYY</Form.Text>
  </Form.Group>
);

const EditEmp = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // ===== Permissions =====
  const showSalary = canSeeSalary();
  const showRole = canChangeRole();

  const [employee, setEmployee] = useState({
    name: "", username: "", role: "", email: "", department: "",
    joinDate: "", birthday: "", phone: "", country: "", city: "",
    postalCode: "", address: "", status: "", maritalstatus: "",
    jobTitle: "", workExperience: "", workDescription: "", gender: "",
    salary: "", hourlyRate: "",
    nextOfKinName: "", nextOfKinRelationship: "",
    nextOfKinPhone: "", nextOfKinEmail: "", nextOfKinAddress: "",
  });

  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState("");
  const [documents, setDocuments] = useState([]);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [editingGroupKey, setEditingGroupKey] = useState(null);
  const [newDocument, setNewDocument] = useState({
    name: "",
    startDate: "",
    expiryDate: "",
    files: [],
    fileNames: [],
    type: ""
  });
  const [departments, setDepartments] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const statusOptions = ['active', 'inactive'];
  const maritalstatusOptions = ['Single', 'Married'];
  const roleOptions = ["Admin", "Employee", "HR", "Accounts"];
  const genderOptions = ["Male", "Female", "Other"];

  const documentTypeOptions = [
    "ID Documents",
    "Company Data Protection",
    "Handbook and Employment",
    "Health and Safety",
    "Additional Document"
  ];

  const shouldHideDates = (type) => {
    return type && !["ID Documents", "Additional Document"].includes(type);
  };

  const formatToUKDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatForBackend = (dateString) => {
    if (!dateString) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      const parts = dateString.split('/');
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${year}-${month}-${day}`;
    }
    return dateString;
  };

  const urlToFile = async (url, filename) => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new File([blob], filename, { type: blob.type });
  };

  useEffect(() => {
    const fetchEmployee = async () => {
      try {
        const response = await axios.get(`https://backend.ems.uk.hiradentallab.com/api/user/user/${userId}`);
        const employeeData = response.data;
        if (employeeData.joinDate) employeeData.joinDate = formatToUKDate(employeeData.joinDate);
        if (employeeData.birthday) employeeData.birthday = formatToUKDate(employeeData.birthday);

        setEmployee({
          ...employeeData,
          department: employeeData.department?._id || employeeData.department || "",
          // For staff the backend does not send these, so they stay empty
          salary: employeeData.salary !== undefined && employeeData.salary !== null
            ? employeeData.salary.toString()
            : "",
          hourlyRate: employeeData.hourlyRate !== undefined && employeeData.hourlyRate !== null
            ? employeeData.hourlyRate.toString()
            : "",
        });

        setProfilePhotoPreview(employeeData.profilePhoto ? `https://backend.ems.uk.hiradentallab.com/${employeeData.profilePhoto}` : "");

        if (employeeData.documents && Array.isArray(employeeData.documents)) {
          const flatDocs = employeeData.documents.map(doc => {
            const fileName = doc.file ? doc.file.split('/').pop() : (doc.name || "file");
            return {
              name: doc.name || "",
              startDate: doc.startDate ? formatToUKDate(doc.startDate) : "",
              expiryDate: doc.expiryDate ? formatToUKDate(doc.expiryDate) : "",
              type: doc.type || "Additional Document",
              file: null,
              existingFile: doc.file || null,
              fileName: fileName
            };
          });
          setDocuments(flatDocs);
        }
      } catch (error) {
        console.error("Error fetching employee data:", error);
      } finally {
        setLoaded(true);
      }
    };

    const fetchDepartments = async () => {
      try {
        const response = await axios.get("https://backend.ems.uk.hiradentallab.com/api/departments/");
        setDepartments(response.data);
      } catch (error) {
        console.error("Error fetching departments:", error);
      }
    };

    fetchEmployee();
    fetchDepartments();
  }, [userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEmployee(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (name, value) => {
    const filtered = value.replace(/[^0-9/]/g, '');
    let formatted = filtered;
    if (filtered.length > 2 && filtered[2] !== '/') formatted = filtered.slice(0, 2) + '/' + filtered.slice(2);
    if (formatted.length > 5 && formatted[5] !== '/') formatted = formatted.slice(0, 5) + '/' + formatted.slice(5);
    if (formatted.length > 10) formatted = formatted.slice(0, 10);
    setEmployee(prev => ({ ...prev, [name]: formatted }));
  };

  const handleProfilePhotoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setProfilePhoto(file);
      const reader = new FileReader();
      reader.onload = () => setProfilePhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const groupDocuments = (docs) => {
    const groups = {};
    docs.forEach(doc => {
      const key = `${doc.name}|${doc.type}|${doc.startDate}|${doc.expiryDate}`;
      if (!groups[key]) {
        groups[key] = {
          name: doc.name,
          type: doc.type,
          startDate: doc.startDate,
          expiryDate: doc.expiryDate,
          files: []
        };
      }
      const displayName = doc.fileName || (doc.existingFile ? doc.existingFile.split('/').pop() : "Unnamed file");
      groups[key].files.push({
        fileName: displayName,
        existingFile: doc.existingFile,
        file: doc.file
      });
    });
    return Object.values(groups);
  };

  const groupedDocuments = groupDocuments(documents);

  const openDocumentModal = (group = null) => {
    if (group) {
      setNewDocument({
        name: group.name,
        startDate: group.startDate,
        expiryDate: group.expiryDate,
        files: [],
        fileNames: [],
        type: group.type
      });
      setEditingGroupKey(`${group.name}|${group.type}|${group.startDate}|${group.expiryDate}`);
    } else {
      setNewDocument({
        name: "",
        startDate: "",
        expiryDate: "",
        files: [],
        fileNames: [],
        type: ""
      });
      setEditingGroupKey(null);
    }
    setShowDocumentModal(true);
  };

  const closeDocumentModal = () => {
    setShowDocumentModal(false);
    setNewDocument({
      name: "",
      startDate: "",
      expiryDate: "",
      files: [],
      fileNames: [],
      type: ""
    });
    setEditingGroupKey(null);
  };

  const handleDocumentChange = (e) => {
    const { name, value } = e.target;
    setNewDocument(prev => ({ ...prev, [name]: value }));
  };

  const handleDocumentFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setNewDocument(prev => ({
        ...prev,
        files: [...prev.files, ...newFiles],
        fileNames: [...prev.fileNames, ...newFiles.map(f => f.name)]
      }));
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeFileFromNewDocument = (index) => {
    setNewDocument(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
      fileNames: prev.fileNames.filter((_, i) => i !== index)
    }));
  };

  const addOrUpdateDocument = () => {
    if (!newDocument.name || !newDocument.type) {
      alert("Document name and type are required!");
      return;
    }

    if (editingGroupKey) {
      let updatedDocs;
      if (newDocument.files.length > 0) {
        updatedDocs = newDocument.files.map(file => ({
          name: newDocument.name,
          startDate: newDocument.startDate,
          expiryDate: newDocument.expiryDate,
          type: newDocument.type,
          file: file,
          fileName: file.name,
          existingFile: null
        }));
      } else {
        updatedDocs = documents
          .filter(doc => {
            const key = `${doc.name}|${doc.type}|${doc.startDate}|${doc.expiryDate}`;
            return key === editingGroupKey;
          })
          .map(doc => ({
            ...doc,
            name: newDocument.name,
            startDate: newDocument.startDate,
            expiryDate: newDocument.expiryDate,
            type: newDocument.type
          }));
      }

      const filteredDocs = documents.filter(doc => {
        const key = `${doc.name}|${doc.type}|${doc.startDate}|${doc.expiryDate}`;
        return key !== editingGroupKey;
      });

      setDocuments([...filteredDocs, ...updatedDocs]);
    } else {
      if (newDocument.files.length === 0) {
        alert("Please select at least one file.");
        return;
      }
      const newDocs = newDocument.files.map(file => ({
        name: newDocument.name,
        startDate: newDocument.startDate,
        expiryDate: newDocument.expiryDate,
        type: newDocument.type,
        file: file,
        fileName: file.name,
        existingFile: null
      }));
      setDocuments(prev => [...prev, ...newDocs]);
    }
    closeDocumentModal();
  };

  const removeDocumentGroup = (group) => {
    setDocuments(prev => prev.filter(doc => {
      const key = `${doc.name}|${doc.type}|${doc.startDate}|${doc.expiryDate}`;
      return key !== `${group.name}|${group.type}|${group.startDate}|${group.expiryDate}`;
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData();

    if (employee.name) formData.append("name", employee.name);
    if (employee.username) formData.append("username", employee.username);
    if (employee.email) formData.append("email", employee.email);
    if (showRole && employee.role) formData.append("role", employee.role);
    if (employee.department) formData.append("department", employee.department);
    if (employee.jobTitle) formData.append("jobTitle", employee.jobTitle);
    if (employee.phone) formData.append("phone", employee.phone);
    if (employee.country) formData.append("country", employee.country);
    if (employee.city) formData.append("city", employee.city);
    if (employee.postalCode) formData.append("postalCode", employee.postalCode);
    if (employee.address) formData.append("address", employee.address);
    if (employee.status) formData.append("status", employee.status);
    if (employee.maritalstatus) formData.append("maritalstatus", employee.maritalstatus);
    if (employee.gender) formData.append("gender", employee.gender);
    if (employee.workExperience) formData.append("workExperience", employee.workExperience);
    if (employee.workDescription) formData.append("workDescription", employee.workDescription);

    // Salary fields only for users allowed to see them
    if (showSalary) {
      if (employee.salary) formData.append("salary", employee.salary.toString().replace('£', '').trim());
      if (employee.hourlyRate !== undefined && employee.hourlyRate !== "") {
        formData.append("hourlyRate", employee.hourlyRate.toString().replace('£', '').trim());
      }
    }

    if (employee.joinDate) formData.append("joinDate", formatForBackend(employee.joinDate));
    if (employee.birthday) formData.append("birthday", formatForBackend(employee.birthday));

    if (employee.nextOfKinName) formData.append("nextOfKinName", employee.nextOfKinName);
    if (employee.nextOfKinRelationship) formData.append("nextOfKinRelationship", employee.nextOfKinRelationship);
    if (employee.nextOfKinPhone) formData.append("nextOfKinPhone", employee.nextOfKinPhone);
    if (employee.nextOfKinEmail) formData.append("nextOfKinEmail", employee.nextOfKinEmail);
    if (employee.nextOfKinAddress) formData.append("nextOfKinAddress", employee.nextOfKinAddress);

    const newPasswordField = e.target.newPassword?.value?.trim();
    const newPinField = e.target.newPin?.value?.trim();
    if (newPasswordField) formData.append("newPassword", newPasswordField);
    if (newPinField) formData.append("newPin", newPinField);

    if (profilePhoto) formData.append("profilePhoto", profilePhoto);

    const documentsWithFiles = await Promise.all(
      documents.map(async (doc) => {
        let fileToUpload = doc.file;
        if (!fileToUpload && doc.existingFile) {
          try {
            const fullUrl = `https://backend.ems.uk.hiradentallab.com/${doc.existingFile}`;
            const filename = doc.fileName || doc.existingFile.split('/').pop();
            fileToUpload = await urlToFile(fullUrl, filename);
          } catch (err) {
            console.warn("Could not fetch existing file, will be omitted", err);
          }
        }
        return { ...doc, fileToUpload };
      })
    );

    const documentsMetadata = documentsWithFiles.map(doc => ({
      name: doc.name,
      startDate: formatForBackend(doc.startDate),
      expiryDate: formatForBackend(doc.expiryDate),
      type: doc.type || "Additional Document"
    }));
    formData.append("documentsMetadata", JSON.stringify(documentsMetadata));

    documentsWithFiles.forEach((doc, idx) => {
      if (doc.fileToUpload) {
        formData.append(`documentFiles[${idx}]`, doc.fileToUpload);
      }
    });

    try {
      await axios.put(
        `https://backend.ems.uk.hiradentallab.com/api/user/update/${userId}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setShowAlert(true);
      setTimeout(() => navigate("/emp-info"), 1500);
    } catch (error) {
      console.error("Update Error:", error.response?.data || error);
      const errorMessage = error.response?.data?.message || "Failed to update employee. Please try again.";
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Staff are not allowed to edit Admin accounts
  if (loaded && !canEditEmployee(employee)) {
    return (
      <div className="container-fluid">
        <Alert variant="warning" className="mt-5">
          You don't have permission to edit Admin accounts.
        </Alert>
        <Button variant="secondary" onClick={() => navigate("/emp-info")}>Back to Employee Information</Button>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <h1 style={{ color: "#0353a4", fontSize: "2rem" }}>
        Edit {employee.name ? `${employee.name}'s` : "Employee"} Info
      </h1>
      <div className="edit-emp-container">
        {showAlert && (
          <Alert variant="success" onClose={() => setShowAlert(false)} dismissible>
            Employee updated successfully!
          </Alert>
        )}
        <Form onSubmit={handleSubmit} className="edit-emp-form">
          {/* Login Details */}
          <Row className="mb-3">
            <Form.Group as={Col} xs={12} md={6} controlId="name">
              <Form.Label>Name:</Form.Label>
              <Form.Control type="text" name="name" value={employee.name} onChange={handleChange} required />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={6} controlId="username">
              <Form.Label>Username:</Form.Label>
              <Form.Control type="text" name="username" value={employee.username} onChange={handleChange} required />
            </Form.Group>
            {showRole && (
              <Form.Group as={Col} xs={12} md={6} controlId="role">
                <Form.Label>Role:</Form.Label>
                <Form.Control as="select" name="role" value={employee.role} onChange={handleChange} required>
                  <option value="">Select Role</option>
                  {roleOptions.map(role => <option key={role} value={role}>{role}</option>)}
                </Form.Control>
              </Form.Group>
            )}
            <Form.Group as={Col} xs={12} md={6} controlId="email">
              <Form.Label>Email:</Form.Label>
              <Form.Control type="email" name="email" value={employee.email || ""} onChange={handleChange} required />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={6} controlId="newPassword">
              <Form.Label>New Password (leave blank to keep current):</Form.Label>
              <Form.Control type="password" name="newPassword" placeholder="Enter new password" />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={6} controlId="newPin">
              <Form.Label>New PIN (leave blank to keep current):</Form.Label>
              <Form.Control type="password" name="newPin" placeholder="Enter new 4-6 digit PIN" maxLength={6} />
            </Form.Group>
          </Row>

          {/* Employee Information */}
          <div>
            <h1 style={{ color: "#0353a4", fontSize: "2rem", marginTop: "30px" }}>
              <IoInformationCircle /> Employee Information
            </h1>
            <hr className="section-divider" />
          </div>
          <Row className="mb-3">
            <Form.Group as={Col} xs={12} md={6} controlId="department">
              <Form.Label>Department:</Form.Label>
              <Form.Control as="select" name="department" value={employee.department} onChange={handleChange} required>
                <option value="">Select Department</option>
                {departments.map(dept => <option key={dept._id} value={dept._id}>{dept.name}</option>)}
              </Form.Control>
            </Form.Group>
            <Form.Group as={Col} xs={12} md={6} controlId="jobTitle">
              <Form.Label>Job Title:</Form.Label>
              <Form.Control type="text" name="jobTitle" value={employee.jobTitle || ""} onChange={handleChange} required />
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Col xs={12} md={6}>
              <UKDateInput label="Join Date" value={employee.joinDate || ""} onChange={handleDateChange} name="joinDate" />
            </Col>
            <Col xs={12} md={6}>
              <UKDateInput label="Birthday" value={employee.birthday || ""} onChange={handleDateChange} name="birthday" required />
            </Col>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col} xs={12} md={6} controlId="phone">
              <Form.Label>Phone:</Form.Label>
              <Form.Control type="text" name="phone" value={employee.phone || ""} onChange={handleChange} />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={6} controlId="country">
              <Form.Label>Country:</Form.Label>
              <Form.Control type="text" name="country" value={employee.country || ""} onChange={handleChange} />
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col} xs={12} md={6} controlId="city">
              <Form.Label>City:</Form.Label>
              <Form.Control type="text" name="city" value={employee.city || ""} onChange={handleChange} />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={6} controlId="postalCode">
              <Form.Label>Postal Code:</Form.Label>
              <Form.Control type="text" name="postalCode" value={employee.postalCode || ""} onChange={handleChange} />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={6} controlId="address">
              <Form.Label>Address:</Form.Label>
              <Form.Control type="text" name="address" value={employee.address || ""} onChange={handleChange} />
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col} xs={12} md={6} controlId="status">
              <Form.Label>Status:</Form.Label>
              <Form.Control as="select" name="status" value={employee.status || ""} onChange={handleChange}>
                <option value="">Select Status</option>
                {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </Form.Control>
            </Form.Group>
            <Form.Group as={Col} xs={12} md={6} controlId="maritalstatus">
              <Form.Label>Marital Status:</Form.Label>
              <Form.Control as="select" name="maritalstatus" value={employee.maritalstatus || ""} onChange={handleChange}>
                <option value="">Select Marital Status</option>
                {maritalstatusOptions.map(ms => <option key={ms} value={ms}>{ms}</option>)}
              </Form.Control>
            </Form.Group>
            <Form.Group as={Col} xs={12} md={6} controlId="workExperience">
              <Form.Label>Work Experience:</Form.Label>
              <Form.Control type="text" name="workExperience" value={employee.workExperience || ""} onChange={handleChange} />
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col} xs={12} md={6} controlId="workDescription">
              <Form.Label>Work Description:</Form.Label>
              <Form.Control as="textarea" rows={3} name="workDescription" value={employee.workDescription || ""} onChange={handleChange} />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={6} controlId="gender">
              <Form.Label>Gender:</Form.Label>
              <Form.Control as="select" name="gender" value={employee.gender || ""} onChange={handleChange}>
                <option value="">Select Gender</option>
                {genderOptions.map(g => <option key={g} value={g}>{g}</option>)}
              </Form.Control>
            </Form.Group>

            {/* ===== Salary + Hourly Rate (hidden for staff) ===== */}
            {showSalary && (
              <>
                <Form.Group as={Col} xs={12} md={6} controlId="salary">
                  <Form.Label>Salary (£):</Form.Label>
                  <Form.Control
                    type="text"
                    name="salary"
                    value={employee.salary ? `£${employee.salary}` : ""}
                    onChange={(e) => {
                      const val = e.target.value.replace('£', '').trim();
                      setEmployee(prev => ({ ...prev, salary: val }));
                    }}
                    required
                  />
                </Form.Group>

                <Form.Group as={Col} xs={12} md={6} controlId="hourlyRate">
                  <Form.Label>Hourly Rate (£):</Form.Label>
                  <Form.Control
                    type="text"
                    name="hourlyRate"
                    value={employee.hourlyRate ? `£${employee.hourlyRate}` : ""}
                    onChange={(e) => {
                      const val = e.target.value.replace('£', '').trim();
                      setEmployee(prev => ({ ...prev, hourlyRate: val }));
                    }}
                    placeholder="£ e.g. 12.71"
                  />
                </Form.Group>
              </>
            )}
          </Row>

          {/* Next of Kin */}
          <div>
            <h1 style={{ color: "#0353a4", fontSize: "2rem", marginTop: "30px" }}>Next of Kin Information</h1>
            <hr className="section-divider" />
          </div>
          <Row className="mb-3">
            <Form.Group as={Col} xs={12} md={6} controlId="nextOfKinName">
              <Form.Label>Next of Kin Name:</Form.Label>
              <Form.Control type="text" name="nextOfKinName" value={employee.nextOfKinName || ""} onChange={handleChange} placeholder="Enter Next of Kin Name" />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={6} controlId="nextOfKinRelationship">
              <Form.Label>Relationship:</Form.Label>
              <Form.Control type="text" name="nextOfKinRelationship" value={employee.nextOfKinRelationship || ""} onChange={handleChange} placeholder="e.g., Spouse, Parent, Sibling" />
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col} xs={12} md={6} controlId="nextOfKinPhone">
              <Form.Label>Next of Kin Phone:</Form.Label>
              <Form.Control type="tel" name="nextOfKinPhone" value={employee.nextOfKinPhone || ""} onChange={handleChange} placeholder="Enter Next of Kin Phone Number" />
            </Form.Group>
            <Form.Group as={Col} xs={12} md={6} controlId="nextOfKinEmail">
              <Form.Label>Next of Kin Email:</Form.Label>
              <Form.Control type="email" name="nextOfKinEmail" value={employee.nextOfKinEmail || ""} onChange={handleChange} placeholder="Enter Next of Kin Email" />
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col} xs={12} md={12} controlId="nextOfKinAddress">
              <Form.Label>Next of Kin Address:</Form.Label>
              <Form.Control type="text" name="nextOfKinAddress" value={employee.nextOfKinAddress || ""} onChange={handleChange} placeholder="Enter Next of Kin Address" />
            </Form.Group>
          </Row>

          {/* Documents Section */}
          <div className="documents-section">
            <div className="section-header">
              <h1 style={{ color: "#0353a4", fontSize: "2rem", marginTop: "30px", marginBottom: "0" }}>📄 Documents</h1>
              <hr className="section-divider" />
            </div>
            <div className="documents-container">
              <Row className="mb-3">
                <Form.Group as={Col} xs={12} md={6} className="doc-upload-group">
                  <Form.Label className="doc-label">
                    <MdPerson className="doc-icon" /> Profile Photo:
                  </Form.Label>
                  {profilePhotoPreview && (
                    <div className="current-photo-link mb-2">
                      <a href={profilePhotoPreview} target="_blank" rel="noopener noreferrer">View Current Photo</a>
                    </div>
                  )}
                  <Form.Control type="file" accept=".png,.jpg,.jpeg" onChange={handleProfilePhotoChange} className="doc-file-input" />
                </Form.Group>
              </Row>

              <Row className="mb-2">
                <Col>
                  <h5 style={{ color: "#0353a4" }}>Add Documents</h5>
                </Col>
              </Row>

              <Row className="mb-4">
                <Form.Group as={Col} xs={12} md={8} lg={6}>
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={() => openDocumentModal()}
                    className="w-100 py-2"
                    style={{ fontSize: "1.1rem" }}
                  >
                    <MdAdd size={20} className="me-1" /> Upload Document
                  </Button>
                </Form.Group>
              </Row>

              {groupedDocuments.length > 0 && (
                <div className="documents-list mt-4">
                  <h5 className="documents-list-title">Added Documents:</h5>
                  <div className="documents-grid">
                    {groupedDocuments.map((group, idx) => (
                      <div key={idx} className="document-card">
                        <div className="document-card-content">
                          <div className="document-type-badge">{group.type}</div>
                          <div className="document-name">{group.name}</div>
                          {!shouldHideDates(group.type) && (
                            <div className="document-dates">
                              <span>Start: {group.startDate || "N/A"}</span>
                              <span>Expiry: {group.expiryDate || "N/A"}</span>
                            </div>
                          )}
                          <div className="document-files mt-2">
                            <small className="text-secondary">Files:</small>
                            <ul className="mb-0 ps-3">
                              {group.files.map((file, i) => (
                                <li key={i}>
                                  {file.existingFile ? (
                                    <a href={`https://backend.ems.uk.hiradentallab.com/${file.existingFile}`} target="_blank" rel="noopener noreferrer">
                                      {file.fileName}
                                    </a>
                                  ) : (
                                    file.fileName
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                        <div className="document-card-actions">
                          <Button variant="outline-primary" size="sm" onClick={() => openDocumentModal(group)}>
                            <MdEdit />
                          </Button>
                          <Button variant="outline-danger" size="sm" onClick={() => removeDocumentGroup(group)}>
                            <MdDelete />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <Button variant="primary" type="submit" disabled={isSubmitting} className="mt-4 submit-button">
            {isSubmitting ? "Updating..." : "Update Employee"}
          </Button>
        </Form>
      </div>

      {/* Document Modal */}
      <Modal show={showDocumentModal} onHide={closeDocumentModal} centered className="document-modal">
        <Modal.Header closeButton>
          <Modal.Title>{editingGroupKey ? "Edit Document Group" : "Add Documents"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Document Type <span className="required">*</span></Form.Label>
              <Form.Select
                value={newDocument.type}
                onChange={(e) => setNewDocument(prev => ({ ...prev, type: e.target.value }))}
                required
              >
                <option value="">-- Select Document Type --</option>
                {documentTypeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Document Name <span className="required">*</span></Form.Label>
              <Form.Control type="text" name="name" value={newDocument.name} onChange={handleDocumentChange} placeholder="Enter Document Name" required />
            </Form.Group>

            {!shouldHideDates(newDocument.type) && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="startDate"
                      value={newDocument.startDate}
                      onChange={handleDocumentChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Expiry Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="expiryDate"
                      value={newDocument.expiryDate}
                      onChange={handleDocumentChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Files {!editingGroupKey && <span className="required">*</span>}</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleDocumentFileChange}
                multiple
                ref={fileInputRef}
                style={{ display: "none" }}
              />
              <Button
                variant="primary"
                onClick={() => fileInputRef.current?.click()}
                className="mb-2"
              >
                <MdCloudUpload className="me-1" /> Choose Files
              </Button>

              {newDocument.fileNames.length > 0 && (
                <>
                  <div className="border rounded p-2 mb-2" style={{ maxHeight: "150px", overflowY: "auto" }}>
                    {newDocument.fileNames.map((name, idx) => (
                      <div key={idx} className="d-flex justify-content-between align-items-center py-1">
                        <small className="text-truncate" style={{ maxWidth: "80%" }}>{name}</small>
                        <Button
                          variant="link"
                          size="sm"
                          className="text-danger p-0"
                          onClick={() => removeFileFromNewDocument(idx)}
                        >
                          <MdClose />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <MdAdd /> Add More Files
                  </Button>
                </>
              )}

              {editingGroupKey && (
                <Form.Text className="text-muted d-block mt-2">
                  {newDocument.files.length > 0
                    ? "New files will replace existing ones when you update."
                    : "Leave empty to keep current files."}
                </Form.Text>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDocumentModal}>Cancel</Button>
          <Button variant="primary" onClick={addOrUpdateDocument}>
            {editingGroupKey ? "Update" : "Add"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EditEmp;